import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getAllMixAndMatches,
  getSingleMixAndMatch,
  createMixAndMatch,
  updateMixAndMatch,
  deleteMixAndMatch,
  toggleMixAndMatchStatus,
} from "@/api/new-api/staff-management/menu-manage/mix-and-match";

// Thunks

export const fetchAllMixAndMatches = createAsyncThunk(
  "mixAndMatches/fetchAll",
  async (restaurant_id, { rejectWithValue }) => {
    const res = await getAllMixAndMatches(restaurant_id);
    if (res.status === "success") return res.data.mix_and_matches;
    return rejectWithValue(res.message);
  }
);

export const fetchSingleMixAndMatch = createAsyncThunk(
  "mixAndMatches/fetchOne",
  async (id, { rejectWithValue }) => {
    const res = await getSingleMixAndMatch(id);
    if (res.status === "success") return res.data.mix_and_match;
    return rejectWithValue(res.message);
  }
);

export const createNewMixAndMatch = createAsyncThunk(
  "mixAndMatches/create",
  async (data, { rejectWithValue }) => {
    const res = await createMixAndMatch(data);
    if (res.status === "success") return res.data.mix_and_match;
    return rejectWithValue(res.message);
  }
);

export const updateExistingMixAndMatch = createAsyncThunk(
  "mixAndMatches/update",
  async ({ id, data }, { rejectWithValue }) => {
    const res = await updateMixAndMatch(id, data);
    if (res.status === "success") return res.data.mix_and_match;
    return rejectWithValue(res.message);
  }
);

export const toggleMixAndMatchStatusAction = createAsyncThunk(
  "mixAndMatches/toggleStatus",
  async (id, { rejectWithValue }) => {
    const res = await toggleMixAndMatchStatus(id);
    if (res.status === "success") return res.data;
    return rejectWithValue(res.message);
  }
);

export const deleteMixAndMatchById = createAsyncThunk(
  "mixAndMatches/delete",
  async (id, { rejectWithValue }) => {
    const res = await deleteMixAndMatch(id);
    if (res.status === "success") return id;
    return rejectWithValue(res.message);
  }
);

// Initial State
const initialState = {
  mixAndMatches: [],
  singleMixAndMatch: null,

  getLoading: false,
  createLoading: false,
  updateLoading: false,
  deleteLoading: false,

  success: null,
  error: null,
  requestStatus: false,
};

// Slice
const mixAndMatchSlice = createSlice({
  name: "mixAndMatches",
  initialState,
  reducers: {
    clearMixAndMatchMessages: (state) => {
      state.success = null;
      state.error = null;
      state.requestStatus = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch All
      .addCase(fetchAllMixAndMatches.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchAllMixAndMatches.fulfilled, (state, action) => {
        state.getLoading = false;
        state.mixAndMatches = action.payload || [];
        state.requestStatus = true;
      })
      .addCase(fetchAllMixAndMatches.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Fetch Single
      .addCase(fetchSingleMixAndMatch.pending, (state) => {
        state.getLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(fetchSingleMixAndMatch.fulfilled, (state, action) => {
        state.getLoading = false;
        state.singleMixAndMatch = action.payload;
        state.requestStatus = true;
      })
      .addCase(fetchSingleMixAndMatch.rejected, (state, action) => {
        state.getLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Create
      .addCase(createNewMixAndMatch.pending, (state) => {
        state.createLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(createNewMixAndMatch.fulfilled, (state, action) => {
        state.createLoading = false;
        if (action.payload) {
          state.mixAndMatches.push(action.payload);
        }
        state.success = "Mix and Match item created successfully!";
        state.requestStatus = true;
      })
      .addCase(createNewMixAndMatch.rejected, (state, action) => {
        state.createLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Update
      .addCase(updateExistingMixAndMatch.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(updateExistingMixAndMatch.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.mixAndMatches = state.mixAndMatches.map((item) =>
          item.id === action.payload.id ? action.payload : item
        );
        state.singleMixAndMatch = action.payload;
        state.success = "Mix and Match item updated successfully!";
        state.requestStatus = true;
      })
      .addCase(updateExistingMixAndMatch.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Toggle Status
      .addCase(toggleMixAndMatchStatusAction.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(toggleMixAndMatchStatusAction.fulfilled, (state, action) => {
        state.updateLoading = false;
        const { id, status } = action.payload || {};
        state.mixAndMatches = state.mixAndMatches.map((item) =>
          item.id === id ? { ...item, status } : item
        );
        state.success = "Mix and Match status updated successfully!";
        state.requestStatus = true;
      })
      .addCase(toggleMixAndMatchStatusAction.rejected, (state, action) => {
        state.updateLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      })

      // Delete
      .addCase(deleteMixAndMatchById.pending, (state) => {
        state.deleteLoading = true;
        state.error = null;
        state.requestStatus = false;
      })
      .addCase(deleteMixAndMatchById.fulfilled, (state, action) => {
        state.deleteLoading = false;
        state.mixAndMatches = state.mixAndMatches.filter(
          (item) => item.id !== action.payload
        );
        state.success = "Mix and Match item deleted successfully!";
        state.requestStatus = true;
      })
      .addCase(deleteMixAndMatchById.rejected, (state, action) => {
        state.deleteLoading = false;
        state.error = action.payload;
        state.requestStatus = false;
      });
  },
});

export const { clearMixAndMatchMessages } = mixAndMatchSlice.actions;
export default mixAndMatchSlice.reducer;
